import moment from "moment";
import { ActionTypes } from "./OrderFlowActionTypes";
/**
 * This function combines `newMeals` and `existingMeals`. It ensures:
 * - Meals from `existingMeals` are retained and take precedence when possible, preserving their quantities and assignments.
 * - Meals for dates not present in `existingMeals` are added from `newMeals`.
 * - Any meals in `existingMeals` that are not part of `newMeals` (e.g., meals separate from the template) are included in the final list.
 *
 * @param {Meal[]} newMeals - An array of new meals generated from the template.
 * @param {Meal[]} existingMeals - An array of existing meals.
 * @param {moment.Moment[]} mealDatesForOrderPeriod - A list of dates for the order period.
 *
 * @returns {Meal[]} - A reconciled array of meals, combining `newMeals` and `existingMeals`, updated to reflect changes and preserve manual additions.
 *
 */
export const mergeNewAndExistingMeals = (newMeals, existingMeals, mealDatesForOrderPeriod) => {
    // Step 1: Filter existingMeals to include only those matching the specified meal dates.
    const existingMealsForMealDates = existingMeals.filter((existingMeal) => mealDatesForOrderPeriod.some((date) => moment(existingMeal.MealDate).isSame(date, "day")));
    // Step 2: Group the filtered existingMeals by their MealDate.
    const existingMealsByDate = existingMealsForMealDates.reduce((acc, existingMeal) => {
        const date = moment(existingMeal.MealDate).format("YYYY-MM-DD");
        acc[date] = acc[date] || [];
        acc[date].push(existingMeal);
        return acc;
    }, {});
    // Step 3: Map over the newMeals to:
    // - Merge newMeal with their matching existingMeal (to maintain assignments).
    // - Keep unmatched, new meals unchanged.
    const updatedMeals = newMeals.map((meal) => {
        var _a, _b;
        const date = moment(meal.MealDate).format("YYYY-MM-DD");
        const existingMealsForDate = existingMealsByDate[date] || [];
        // Find a matching meal for the same date and ProgramMealId
        const matchingMeal = existingMealsForDate.find((existingMeal) => existingMeal.ProgramMealId === meal.ProgramMealId && moment(meal.MealDate).isSame(existingMeal.MealDate, "day"));
        // Preserve user's quantity and recipe assignments from existing meal
        if (matchingMeal) {
            // Detect if user has adjusted this meal (removed assignments or manually selected recipes)
            const hasUserCustomizations = (matchingMeal.NumRemovedAssignments && matchingMeal.NumRemovedAssignments > 0) ||
                ((_b = (_a = matchingMeal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.some((ra) => !ra.IsAutomaticSelection)) !== null && _b !== void 0 ? _b : false);
            // Use user's quantity if they customized, otherwise use template quantity
            const quantityToUse = hasUserCustomizations ? matchingMeal.Quantity : meal.Quantity;
            return Object.assign(Object.assign({}, meal), { Quantity: quantityToUse, NumRemovedAssignments: matchingMeal.NumRemovedAssignments, RecipeAssignments: matchingMeal.RecipeAssignments });
        }
        // Otherwise, return the meal unchanged
        return meal;
    });
    const mergedMeals = [...updatedMeals];
    // Step 4: Add any meals from existingMeals that are not already in newMeals.
    // - This occurs when a meal is added that is not part of the template so it will not be in the newMeals array.
    Object.entries(existingMealsByDate).forEach(([date, mealsByProgramId]) => {
        const newMealsForExistingDate = newMeals.filter((nm) => moment(nm.MealDate).isSame(date, "day"));
        if (newMealsForExistingDate.length === 0) {
            mealsByProgramId.forEach((existingMeal) => {
                const alreadyInMerged = mergedMeals.some((m) => moment(m.MealDate).isSame(existingMeal.MealDate, "day") && m.ProgramMealId === existingMeal.ProgramMealId);
                if (!alreadyInMerged) {
                    mergedMeals.push(existingMeal);
                }
            });
        }
    });
    return mergedMeals.filter(meal => meal.Quantity > 0);
};
/**
 * Calculates the sub period based on the day quantity and days of week.
 *
 * This function prevents dividing by zero when `days` is empty.
 *
 * To avoid this, we explicitly check if `days.length > 0`:
 * - If there are days, perform the division and round up.
 * - If not, return a fallback value
 *
 * @param {number} dayQuantity - Total number of days.
 * @param {number[]} days - Days for the week.
 * @returns {number} The calculated sub period, or a safe fallback if no days exist.
 */
export const calculateSubscriptionPeriod = (dayQuantity, days) => {
    return days.length > 0 ? Math.ceil(dayQuantity / days.length) : 1;
};
export const setMealsSkippedForDay = (dispatch, meals, day, isSkipped) => {
    dispatch({
        type: ActionTypes.SET_MEALS,
        payload: meals.map((meal) => (moment(meal.MealDate).isSame(day) ? Object.assign(Object.assign({}, meal), { IsSkipped: isSkipped }) : meal)),
    });
};
